import { SnackBarM } from "@common/components/alert/snackbar/SnackBar"
import useLiveKitEventSender from "@common/hooks/livekit/useLiveKitEventSender"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { clearPendingAttachments } from "@data/redux/actions/activity"
import { apiRequest } from "@data/redux/actions/api"
import { activitiesService } from "@data/services/activitiesService"
import { buildPendingAttachmentsBody } from "@data/utility/pendingAttachmentsUtils"
import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

// Finalize previous activity silently when current activity changes and previous had media
// Args:
// - currentActivityId: number | null - id of the activity currently displayed
// - chronoDuration: number - current chrono duration to pass to finalize
// - hasMediaAndSynced: boolean - whether the activity has media AND all logs are synced
const useFinalizeSilentlyIfHasMedia = (
    currentActivityId,
    chronoDuration,
    hasMediaAndSynced = false
) => {
    const dispatch = useDispatch()

    const [currentOnScreenActivityId, setCurrentOnScreenActivityId] =
        useState(currentActivityId)
    const [onScreenHasMediaAndSynced, setOnScreenHasMediaAndSynced] =
        useState(hasMediaAndSynced)
    const [onScreenChronoDuration, setOnScreenChronoDuration] =
        useState(chronoDuration)

    const isFinalizingRef = useRef(false)
    const isMountedRef = useRef(true)
    const pendingAttachmentsByActivity = useSelector(
        (state) => state.data?.pendingAttachments?.byActivityId
    )
    const finalizingActivityIdRef = useRef(null)

    useEffect(() => {
        return () => {
            isMountedRef.current = false
        }
    }, [])

    // Update on-screen state when hasMediaAndSynced changes
    useEffect(() => {
        if (isMountedRef.current)
            setOnScreenHasMediaAndSynced(hasMediaAndSynced)
        if (isMountedRef.current) setOnScreenChronoDuration(chronoDuration)
    }, [hasMediaAndSynced])

    // Trigger finalize of previous activity when the focused activity changes.
    // The on-screen activity id must advance on EVERY switch, finalize or not:
    // holding it back while waiting for the media flag meant a later flag flip
    // (belonging to the newly displayed activity) finalized the id left behind
    // — a chrono-only call on an activity with no server-side log, doomed to
    // 403 « pas encore sauvegardé » on each pass.
    useEffect(() => {
        const isSwitchingActivity =
            currentActivityId &&
            currentOnScreenActivityId &&
            currentActivityId !== currentOnScreenActivityId

        if (isSwitchingActivity) {
            const pending = buildPendingAttachmentsBody(
                pendingAttachmentsByActivity?.[currentOnScreenActivityId]
            )
            // Finalize only with something the server can accept: a media log
            // it already holds, or pending tmp keys it can self-heal from.
            // Without either, the call can only 403.
            if (
                (onScreenHasMediaAndSynced || pending) &&
                !isFinalizingRef.current
            ) {
                isFinalizingRef.current = true
                finalizingActivityIdRef.current = currentOnScreenActivityId
                const body = { chrono_duration: onScreenChronoDuration }
                if (pending) body.pending_attachments = pending
                dispatch(
                    apiRequest(
                        activitiesService.finalizeActivitySilent,
                        { id: currentOnScreenActivityId },
                        body
                    )
                )
            }
            setCurrentOnScreenActivityId(currentActivityId)
        } else if (currentOnScreenActivityId == null && currentActivityId) {
            setCurrentOnScreenActivityId(currentActivityId)
        }
    }, [
        currentActivityId,
        currentOnScreenActivityId,
        onScreenHasMediaAndSynced,
        dispatch,
    ])

    const { sendLiveKitData } = useLiveKitEventSender()

    const isLiveMode = useSelector((state) => state.liveMode?.isLiveModeEnabled)
    const user = useSelector((state) => state.data.currentUser?.object)
    const liveSessionId = useSelector((state) => state.liveMode?.liveSessionId)
    const liveSession = useSelector((state) =>
        state.data.currentUser?.object?.live_sessions?.find(
            (x) => x?.id === liveSessionId
        )
    )

    useApiStatus(
        activitiesService.finalizeActivitySilent,
        null,
        false,
        (successData) => {
            isFinalizingRef.current = false
            if (finalizingActivityIdRef.current) {
                dispatch(
                    clearPendingAttachments({
                        activityId: finalizingActivityIdRef.current,
                    })
                )
                finalizingActivityIdRef.current = null
            }
            const skillSets = successData?.skill_sets || []
            const petals =
                skillSets
                    ?.filter(
                        (ss) =>
                            ss?.current_count === 0 &&
                            !ss?.is_last_evolution_read
                    )
                    ?.reduce(
                        (sum, ss) =>
                            sum +
                            (ss?.steps?.[ss?.current_step - 1]?.petals_gained ||
                                0),
                        0
                    ) || 0
            const xpGained = successData?.activity?.xp_gained ?? 0
            let message = `Tu as gagné ⚡ ${xpGained} XP`
            if (petals > 0) {
                message += `  🌸 ${petals} pétales`
            }
            SnackBarM.show({
                text1: message,
                duration: 4000,
            })

            if (isLiveMode) {
                const tutorIds = liveSession?.users
                    ?.filter((l) => l?.is_tutor)
                    ?.map((l) => l?.id)

                sendLiveKitData(
                    "activityFinalized",
                    {
                        displayName: user?.display_name,
                        activityId: finalizingActivityIdRef.current,
                    },
                    tutorIds?.length > 0 ? tutorIds : null
                )
            }
        },
        (errorData) => {
            isFinalizingRef.current = false
            if (
                errorData?.status === 403 &&
                typeof errorData?.message === "string" &&
                errorData.message.includes("déjà été soumise") &&
                finalizingActivityIdRef.current
            ) {
                dispatch(
                    clearPendingAttachments({
                        activityId: finalizingActivityIdRef.current,
                    })
                )
            }
            finalizingActivityIdRef.current = null
        }
    )

    return { currentOnScreenActivityId }
}

export default useFinalizeSilentlyIfHasMedia
